import { Lock } from "lucide-react";
import type { PublicQuote } from "@forgepoint/shared";
import { BackButton } from "@/components/BackButton";
import { QuoteDocumentPreview } from "./QuoteDocumentPreview";
import type { DocumentAssetUrls } from "./types";

interface LockedDocumentViewProps {
  /** The customer-safe quote projection bound into the canvas. */
  publicQuote: PublicQuote;
  /** Resolved signed asset urls keyed by asset id. */
  assetUrls: DocumentAssetUrls;
  /** The stored document HTML to render. */
  documentHtml: string;
  /** Footer text explaining why the document cannot be edited. */
  notice: string;
  /** Closes the view. */
  onClose: () => void;
}

/**
 * Full-height read-only render of a locked quote document, framed by a
 * "Document locked" header and an explanatory footer.
 * @param publicQuote - The customer-safe quote projection
 * @param assetUrls - Resolved signed asset urls keyed by asset id
 * @param documentHtml - The stored document HTML to render
 * @param notice - Footer text explaining why the document cannot be edited
 * @param onClose - Closes the view
 * @returns The locked document view
 */
export function LockedDocumentView({
  publicQuote,
  assetUrls,
  documentHtml,
  notice,
  onClose,
}: LockedDocumentViewProps) {
  return (
    <div className="flex h-full flex-col">
      <header className="flex shrink-0 items-center gap-3 border-b bg-background px-4 py-2">
        <BackButton onClick={onClose} />
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Lock className="size-3.5" aria-hidden /> Document locked
        </span>
      </header>
      <QuoteDocumentPreview
        publicQuote={publicQuote}
        assetUrls={assetUrls}
        documentHtml={documentHtml}
      />
      <p className="flex shrink-0 items-center justify-center gap-1.5 border-t bg-background px-4 py-2 text-xs text-muted-foreground">
        <Lock className="size-3" aria-hidden />
        {notice}
      </p>
    </div>
  );
}
